import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Building2, CalendarDays } from "lucide-react";
import { motion } from "framer-motion";
import { PublicLayout } from "../components/PublicLayout";
import { EventCard } from "../components/EventCard";
import { OrganizerContactBlock } from "../components/OrganizerContactBlock";
import { ShopTeaser } from "../components/shop/ShopTeaser";
import { BasarTeaser } from "../components/basar/BasarTeaser";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import { api } from "../lib/api";
import { useLang } from "../context/LanguageContext";

export default function OrganizerPage() {
  const { id } = useParams();
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api
      .get(`/organizers/${id}`)
      .then((res) => setData(res.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <PublicLayout>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-6">
          <Skeleton className="h-24 rounded-2xl" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(3)].map((_, i) => (
              <Skeleton key={i} className="h-64 rounded-2xl" />
            ))}
          </div>
        </div>
      </PublicLayout>
    );
  }

  if (notFound || !data) {
    return (
      <PublicLayout>
        <div className="max-w-2xl mx-auto px-4 py-24 text-center" data-testid="organizer-not-found">
          <h1 className="text-2xl font-semibold mb-2">{t("Fant ikke arrangøren")}</h1>
          <Button asChild className="mt-4">
            <Link to="/"><ArrowLeft className="w-4 h-4 mr-1.5" />{t("Til forsiden")}</Link>
          </Button>
        </div>
      </PublicLayout>
    );
  }

  const { organizer, events } = data;
  const initials = (organizer.org_name || "A")
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <PublicLayout>
      <section className="hero-decor border-b border-border">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors duration-150 mb-6" data-testid="organizer-back-link">
            <ArrowLeft className="w-4 h-4" />
            {t("Alle arrangementer")}
          </Link>
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} className="flex items-center gap-5">
            <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-ink text-ink-foreground flex items-center justify-center font-heading font-semibold text-2xl shrink-0">
              {initials}
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight" data-testid="organizer-name">{organizer.org_name}</h1>
                <Badge variant="secondary" className="gap-1"><Building2 className="w-3 h-3" /> {t("Arrangør")}</Badge>
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                {organizer.org_number && <span>{t("Org.nr {n}", { n: organizer.org_number })} · </span>}
                {events.length} {events.length === 1 ? t("kommende arrangement") : t("arrangementer")}
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 grid gap-6 lg:grid-cols-12 items-start">
        <OrganizerContactBlock name={organizer.org_name} orgNumber={organizer.org_number} contact={organizer.contact} className="lg:col-span-7 max-w-xl" />
        {/* «Butikken vår» – vises berre når arrangøren har open butikk med aktive varer */}
        <div className="lg:col-span-5 space-y-6">
          <ShopTeaser organizerId={organizer.id} organizerName={organizer.org_name} />
          {/* «Basaren vår» – vises berre når arrangøren har open basar (eller nyleg trekt) */}
          <BasarTeaser organizerId={organizer.id} organizerName={organizer.org_name} />
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <h2 className="text-xl sm:text-2xl font-semibold tracking-tight mb-6 flex items-center gap-2">
          <CalendarDays className="w-5 h-5" />
          {t("Arrangementer fra {name}", { name: organizer.org_name })}
        </h2>
        {events.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border bg-card p-12 text-center" data-testid="organizer-empty-state">
            <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-secondary flex items-center justify-center">
              <CalendarDays className="w-7 h-7 text-muted-foreground" />
            </div>
            <h3 className="font-heading font-semibold text-lg">{t("Ingen publiserte arrangementer akkurat nå")}</h3>
            <p className="text-sm text-muted-foreground mt-1">{t("Kom tilbake senere!")}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="organizer-event-grid">
            {events.map((event) => (
              <EventCard key={event.id} event={event} />
            ))}
          </div>
        )}
      </section>
    </PublicLayout>
  );
}
